import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ChevronLeftIcon,
  ClockIcon,
  UserIcon,
  TagIcon
} from '@heroicons/react/24/outline';

const HelpArticle = ({ article, onBack }) => {
  if (!article) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <button
              onClick={onBack}
              className="flex items-center text-blue-600 hover:text-blue-700 font-medium"
            >
              <ChevronLeftIcon className="h-5 w-5 mr-1" />
              Back to Help Center
            </button>
            <Link
              to="/"
              className="px-4 py-2 text-blue-600 hover:text-blue-700 font-medium"
            >
              Home
            </Link>
          </div>
        </div>
      </header>

      {/* Article Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <article className="bg-white rounded-xl shadow-lg p-8">
          {/* Article Header */}
          <header className="mb-8">
            <div className="flex items-center text-sm text-gray-500 mb-4">
              <TagIcon className="h-4 w-4 mr-1" />
              <span className="mr-4">{article.category}</span>
              <ClockIcon className="h-4 w-4 mr-1" />
              <span className="mr-4">{article.readTime}</span>
              <UserIcon className="h-4 w-4 mr-1" />
              <span>FBRSTAPP Team</span>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              {article.title}
            </h1>
            <p className="text-xl text-gray-600">
              {article.description}
            </p>
          </header>

          {/* Article Body */}
          <div className="prose prose-lg max-w-none">
            {article.content.map((section, index) => (
              <div key={index} className="mb-8">
                {section.type === 'heading' && (
                  <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                    {section.text}
                  </h2>
                )}
                {section.type === 'paragraph' && (
                  <p className="text-gray-700 mb-4 leading-relaxed">
                    {section.text}
                  </p>
                )}
                {section.type === 'list' && (
                  <ul className="list-disc list-inside mb-4 space-y-2">
                    {section.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="text-gray-700">
                        {item}
                      </li>
                    ))}
                  </ul>
                )}
                {section.type === 'code' && (
                  <div className="bg-gray-100 rounded-lg p-4 mb-4">
                    <pre className="text-sm text-gray-800 overflow-x-auto">
                      <code>{section.text}</code>
                    </pre>
                  </div>
                )}
                {section.type === 'warning' && (
                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                    <div className="flex">
                      <div className="ml-3">
                        <p className="text-sm text-yellow-700">
                          <strong>Warning:</strong> {section.text}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                {section.type === 'info' && (
                  <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
                    <div className="flex">
                      <div className="ml-3">
                        <p className="text-sm text-blue-700">
                          <strong>Info:</strong> {section.text}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                {section.type === 'steps' && (
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">
                      Step-by-Step Instructions:
                    </h3>
                    <ol className="list-decimal list-inside space-y-3">
                      {section.steps.map((step, stepIndex) => (
                        <li key={stepIndex} className="text-gray-700">
                          <span className="font-medium">{step.title}:</span> {step.description}
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Related Articles */}
          {article.relatedArticles && article.relatedArticles.length > 0 && (
            <div className="mt-12 pt-8 border-t border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Related Articles
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {article.relatedArticles.map((related, index) => (
                  <div key={index} className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
                    <h4 className="font-medium text-gray-900 mb-2">{related.title}</h4>
                    <p className="text-sm text-gray-600">{related.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Contact Support */}
          <div className="mt-12 pt-8 border-t border-gray-200 text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Still need help?
            </h3>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={`https://wa.me/923080323323?text=Hi%2C%20I%20need%20help%20with%20the%20article%3A%20${encodeURIComponent(article.title)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
              >
                WhatsApp Support
              </a>
              <a
                href="mailto:askcom.pk@gmail.com"
                className="px-6 py-3 border-2 border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 font-medium"
              >
                Email Support
              </a>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
};

export default HelpArticle;